import React, { memo, useEffect, useState } from "react";
import styles from "./MergeListings.module.scss";
import XIconThemed from "@src/assets/general-icons/XIconThemed";
import { Button, ButtonVariant } from "../button/Button";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import _ from "lodash";
import { collapseMarketplaceSync } from "@src/components/utils/syncStatus";
import { VGrid } from "../vGrid/VGrid";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { refreshPricingInventory } from "@src/store/pricing/refreshPricingInventory";
import { columns } from "./columns";
import { groupSeatNumbersWithRange } from "../utils/columnUtils";
import UniversalInput from "../universalInput/UniversalInput";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MergeListingsValidation, MergeListingsValidationType } from "@src/validation/MergeListingsValidation";
import { Input } from "../input/Input";
import { Dropdown } from "../dropdown/Dropdown";
import { clx } from "../utils/stringUtils";
import Checkbox from "../vGrid/Checkbox";
import { formatCurrency } from "../utils/numberUtils";
import { pricingActions } from "@src/store";
import { getInventoryGroupKey, getNewInventoryListingIds } from "../utils/inventoryUtils";
import { toast } from "sonner";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { displayOrDash, getFirstSeatNumber, getUniformValue } from "@src/utils/general.utils";
import MarketplacesV2 from "../marketPlaces/MarketplacesV2";
import { isAnyListingInTMRestrictedCategory, isMarketplaceBlockedByTMConsent } from "../utils/marketplaceUtils";
import { ExportDestination } from "@src/constants/constants";

type TMergeListings = {
	handleClose?: () => void;
};

type TState = {
	splits: number;
	marketplaces: Array<number>;
	useEvenOddSeatNumbering: boolean;
};

const MergeListings: React.FC<TMergeListings> = ({ handleClose }) => {
	const [state, setState] = useState<TState>({
		splits: -1,
		marketplaces: [],
		useEvenOddSeatNumbering: false,
	});

	const dispatch = useAppDispatch();
	const eventInventoryAll = useAppSelector((state) => state.pricing.eventInventoryAll);
	const selectedInventoryIds = useAppSelector((state) => state.pricing.selection.inventoryItemIds);
	const CompanyName = useAppSelector((state) => state.settings.companyName);
	const selectedEventId = useAppSelector((state) => state.pricing.selection.event?.Event_ID);
	const { data: exportDestinationsData } = useAppSelector((state) => state.system.exportDestinations);
	const eventsList = useAppSelector((state) => state.pricing.events.data.Events);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);

	const sortedData = _.sortBy(eventInventoryAll.data, "Listing_ID");
	const groupedData = _.groupBy(sortedData, getInventoryGroupKey);

	const rowsData = _.map(groupedData, (value) => {
		const seatNumbers = value.map((x) => x.Seat_Number);
		return {
			...value[0],
			Seats: groupSeatNumbersWithRange(seatNumbers),
			Quantity: value.length,
			Item_Cost: value.reduce((acc, curr) => acc + curr.Item_Cost, 0),
			...collapseMarketplaceSync(value),
		};
	}).filter((x) => {
		return !x.Invoice_ID;
	});

	const allEventListingIds = rowsData.map((x) => x.Listing_ID);
	const filteredItems = _.filter(rowsData, (x) => selectedInventoryIds.includes(x.Item_ID));
	const EventId = filteredItems[0]?.Event_ID ?? selectedEventId;
	const allListingIds = filteredItems.map((x) => x.Listing_ID);
	const filteredIndividualItems = sortedData.filter((x) => allListingIds.includes(x.Listing_ID) && !x.Invoice_ID);

	const inRestrictedCategory = isAnyListingInTMRestrictedCategory(filteredItems, eventsList);

	const totalQuantity = filteredItems?.reduce((acc, item) => acc + item.Quantity, 0);
	const totalCost = _.sumBy(filteredItems, (x) => x.Item_Cost) / totalQuantity;

	const {
		control,
		handleSubmit,
		watch,
		formState: { isValid, isSubmitting },
	} = useForm<MergeListingsValidationType>({
		resolver: yupResolver(MergeListingsValidation),
		defaultValues: {
			firstSeatNumber: getFirstSeatNumber(filteredItems),
			price: getUniformValue(filteredItems.map((x) => x.Item_Price)),
			row: getUniformValue(filteredItems.map((x) => x.Row)),
			section: getUniformValue(filteredItems.map((x) => x.Section)),
		},
	});

	const price = watch("price");
	const row = watch("row");
	const section = watch("section");
	const firstSeatNumber = watch("firstSeatNumber");

	const seatNumbers =
		firstSeatNumber && firstSeatNumber.length <= 6 && totalQuantity
			? Array.from({ length: totalQuantity }, (_, i) => Number(firstSeatNumber) + i * (state.useEvenOddSeatNumbering ? 2 : 1))
			: [];

	const seatNumbersMapped = seatNumbers.map((x) => {
		return (
			<div key={x} className={styles.item}>
				{x}
			</div>
		);
	});

	const splitItems = [
		{ id: 1, label: "Default", value: -1, onClick: () => setState((prev) => ({ ...prev, splits: -1 })) },
		{ id: 2, label: "Pairs", value: 2, onClick: () => setState((prev) => ({ ...prev, splits: 2 })) },
		{ id: 3, label: "Any", value: 1, onClick: () => setState((prev) => ({ ...prev, splits: 1 })) },
		{ id: 4, label: "None", value: 0, onClick: () => setState((prev) => ({ ...prev, splits: 0 })) },
	];

	const selectedSplit = splitItems.find((x) => x.value === state.splits)?.label;

	const toggleMarketplace = (x: number) => {
		const exists = state.marketplaces.includes(x);
		if (exists) {
			setState((prev) => ({ ...prev, marketplaces: prev.marketplaces.filter((y) => y !== x) }));
			return;
		}

		setState((prev) => ({ ...prev, marketplaces: [...prev.marketplaces, x] }));
	};

	const onSubmit: SubmitHandler<MergeListingsValidationType> = async (data) => {
		const logs: Array<string> = [];

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const track = (label: string, res: any) => {
			if (res?.error) {
				const msg = res.error.message ?? "Unknown error";
				logs.push(`${label}: ${msg}`);
			}
		};

		const updatePricesPayload = filteredItems.map((x) => ({
			listing_id: x.Listing_ID,
			price: Number(data.price),
			face: null,
		}));

		const changeSeatNumbersPayload = filteredIndividualItems.map((x, i) => ({
			ItemId: x.Item_ID,
			SeatNumber: Number(firstSeatNumber) + i * (state.useEvenOddSeatNumbering ? 2 : 1),
			IsOddEven: state.useEvenOddSeatNumbering,
		}));

		const updateListingPositionPayload = {
			NewSection: section,
			NewRow: row,
			ListingIds: allListingIds,
			SectionLocked: false,
			RowLocked: false,
			SeatLocked: false,
		};

		const releaseListingsPayload = {
			ListingIds: allListingIds,
		};

		const res1 = await dispatch(pricingActions.updateListingPrices(updatePricesPayload));
		track("Update Prices", res1);

		const res2 = await dispatch(pricingActions.updateListingPosition(updateListingPositionPayload));
		track("Update Listing Position", res2);

		const res3 = await dispatch(pricingActions.changeTicketsSeatNumbers(changeSeatNumbersPayload));
		track("Change Seat Numbers", res3);

		const res4 = await dispatch(pricingActions.relistListings(releaseListingsPayload));
		track("Relist Listings", res4);

		const response = await getNewInventoryListingIds({ EventId, CompanyName, allEventListingIds });

		if (response.error || !response.data) {
			logs.push(`Fetch Updated Inventory: ${response.error ?? "Unknown error"}`);
			logs.push(`Update Sharing: ${response.error ?? "Unknown error"}`);
		} else {
			const blockedDestinationIds = new Set(
				exportDestinationsData
					.filter((m) => isMarketplaceBlockedByTMConsent(m, inRestrictedCategory))
					.map((m) => m.Export_Destination_ID),
			);

			const updateSharingPayload = {
				ListingIds: response.data,
				disableTooltip: true,
				ShareOutSettings: state.marketplaces
					.filter((x) => !blockedDestinationIds.has(x))
					.filter((x) => hasCIMSMarketAccess || x !== ExportDestination.Cims)
					.map((x) => ({
						Export_Destination_ID: x,
						AllowedSplits: state.splits,
						Visibility: "SHOW" as const,
					})),
			};

			const res5 = await dispatch(pricingActions.updateSharing(updateSharingPayload));
			track("Update Sharing", res5);
		}

		if (logs.length > 0) {
			toast.error(
				<div>
					{logs.map((x) => (
						<div key={x}>{x}</div>
					))}
				</div>,
				{ duration: 8000 },
			);
		}

		dispatch(refreshPricingInventory());

		handleClose?.();
	};

	useEffect(() => {
		const run = async () => {
			const res = await dispatch(pricingActions.getListingSharedSettings(allListingIds));

			if (pricingActions.getListingSharedSettings.fulfilled.match(res)) {
				const first = res.payload[0];
				const selectedExportDestinations = [...new Set(res.payload.map((x) => x.Export_Destination_ID))];

				setState((prev) => ({
					...prev,
					splits: first ? first.AllowedSplits : prev.splits,
					marketplaces: selectedExportDestinations,
				}));
			}
		};

		run();
		// eslint-disable-next-line
	}, [dispatch]);

	const hasEditLockDownCosts = useAppSelector((state) => state.settings.hasEditLockDownCosts);

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.root}>
			<div className={styles.header}>
				<div>Merge Listings</div>
				<div aria-hidden="true" style={{ cursor: "pointer" }} onClick={handleClose}>
					<XIconThemed color="#fafafa" />
				</div>
			</div>
			<EventContextHeader
				padding
				listings={filteredItems.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.content}>
				<div data-is-loading={isSubmitting} className={styles.mergeListingsDetails}>
					<div className={styles.headline}>Merge Listings Details</div>
					<div className={styles.inputsContainer}>
						<div className={styles.containerField}>
							<div>
								<Input
									fixToDecimals
									inputPrefix="$"
									disableErrorMessage
									control={control}
									type="number"
									name="price"
									step={1}
									placeholder="Enter Price"
									label="Price"
								/>
							</div>
							<div className={styles.inputRow}>
								<Input disableErrorMessage control={control} name="section" placeholder="Enter Section" label="Section" />
								<Input disableErrorMessage control={control} name="row" placeholder="Enter Row" label="Row" />
							</div>
							<div className={styles.inputRow}>
								<div>
									<UniversalInput
										value={String(totalQuantity ?? 0)}
										isDisabled
										placeholder="Test"
										onChange={() => void 0}
										label="Quantity"
									/>
								</div>
								<div>
									<Input
										disableErrorMessage
										control={control}
										type="number"
										name="firstSeatNumber"
										placeholder="First Seat Number"
										label="First Seat Number"
									/>
								</div>
							</div>
							<div style={{ width: "calc(50% - 0.8rem)" }}>
								<Dropdown onClick={() => void 0} isDisabled={false} items={splitItems} buttonText={selectedSplit} />
							</div>
						</div>
						<div className={styles.reviewContainerField}>
							<div className={styles.smallHeadline}>Review</div>
							<div className={styles.reviewContainer}>
								<div className={styles.reviewContent}>
									<div className={styles.previewRow}>
										<div className={styles.previewItem}>
											<div className={styles.label}>Section</div>
											<div className={styles.value}>{displayOrDash(section)}</div>
										</div>
										<div className={styles.previewItem}>
											<div className={styles.label}>Row</div>
											<div className={styles.value}>{displayOrDash(row)}</div>
										</div>
										<div className={styles.previewItem}>
											<div className={styles.label}>Quantity</div>
											<div className={styles.value}>{totalQuantity}</div>
										</div>
									</div>
									<div className={styles.previewRow}>
										<div className={styles.previewItem}>
											<div className={styles.label}>Cost</div>
											<div style={{ whiteSpace: "nowrap" }} className={styles.value}>
												{formatCurrency(totalCost, !!totalCost)}
											</div>
										</div>
										<div className={styles.previewItem}>
											<div className={styles.label}>Price</div>
											<div className={styles.value}>{formatCurrency(Number(price), !!price)}</div>
										</div>
									</div>
									<div className={styles.seatNumbersContainer}>
										<div className={styles.text}>Individual Seat Numbers</div>
										<div className={styles.seatNumbersMapped}>{seatNumbersMapped}</div>
									</div>
								</div>
								<div className={styles.reviewInfo}>Merged from {filteredItems?.length} listings</div>
							</div>
						</div>
					</div>
					<div className={styles.bottomDetails}>
						<div className={styles.marketplaces}>
							<div
								style={{ height: 23, flexShrink: 0, display: "flex", alignItems: "center" }}
								className={clx({ [styles.text]: true })}>
								Share on the following marketplaces:
							</div>
						</div>
						<MarketplacesV2
							compactView
							onMarkteplaceClick={toggleMarketplace}
							selectedExportDestinations={state.marketplaces}
							containerClassName={styles.marketplacesContainer}
							inRestrictedCategory={inRestrictedCategory}
						/>
						<div className={styles.marketplaces}>
							<Checkbox
								isChecked={state.useEvenOddSeatNumbering}
								onChange={(x) => setState((prev) => ({ ...prev, useEvenOddSeatNumbering: x }))}
							/>
							<div className={styles.text}>Use odd/even seat numbering</div>
						</div>
					</div>
				</div>
				<div data-is-loading={isSubmitting} className={styles.selectedListings}>
					<div className={styles.headline}>Selected Listings</div>
					<div className={styles.table}>
						<VGrid<EventInventoryRecord>
							getRowNodeId={(row) => row.Item_ID}
							columns={columns(hasEditLockDownCosts)}
							rowsData={filteredItems}
						/>
					</div>
				</div>
			</div>
			<div className={styles.ctaButtons}>
				<Button style={{ padding: "7.5px 16px", width: 81 }} onClick={handleClose} variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button
					isLoading={isSubmitting}
					disabled={!isValid || isSubmitting}
					type="submit"
					style={{ padding: "7.5px 16px", width: 138 }}
					variant={ButtonVariant.Primary}>
					Apply changes
				</Button>
			</div>
		</form>
	);
};

export default memo(MergeListings);
